import React from 'react';
import ReactDOM from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RouterProvider } from 'react-router-dom';
import router from './router';
import { ThemeProvider } from '@mui/material/styles';
import theme from './pages/LazyMall/theme/defaultTheme';
import { HelmetProvider } from 'react-helmet-async';
import MainLoading from './MainLoading';
import { CookiesProvider } from 'react-cookie';
import * as Sentry from '@sentry/react';

const env = import.meta.env.VITE_SET_ENV || 'DEV';

Sentry.init({
    dsn: 'https://9326bdeea8b8c28c4a8b6c982560b649@o4508776190377984.ingest.us.sentry.io/4508804727373824',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: env || 'DEV',
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: env === 'PRD',
    normalizeDepth: 6,
    attachStacktrace: true,
});

if (env === 'PRD') {
    // if (!window.console) {
    //     window.console = {};
    // }
    const methods = ['log', 'debug', 'warn', 'info'];
    for (let i = 0; i < methods.length; i++) {
        window.console[methods[i]] = function () {};
    }
}

// anonymousUser-storage를 localStorage에서 sessionStorage로 이전하는 과정에서
// 보통은 zustand에서 sessionStorage에 있는 데이터를 가져오겠지만 만일의 사태에 대비하여
// localStorage에 남아있는 anonymousUser-storage를 삭제하는 로직을 추가
// 추후 이 로직을 남길지 삭제할 지는 TODO: 로 남김
const anonymousLocalData = localStorage.getItem('anonymousUser-storage');
const anonymousSessionData = sessionStorage.getItem('anonymousUser-storage');
if (anonymousSessionData) {
    if (anonymousLocalData) {
        localStorage.removeItem('anonymousUser-storage');
    }
}

window.addEventListener('error', (e) => {
    const errorInfo = {
        message: e.error?.message || e.message || 'Unknown error',
        filename: e.filename || 'Unknown file',
        lineno: e.lineno || 0,
        colno: e.colno || 0,
        stack: e.error?.stack || 'No stack trace available',
        timestamp: new Date().toLocaleString('ko-KR'),
        userAgent: navigator.userAgent,
        url: window.location.href,
        type: e.error?.name || 'Error',
        trusted: e.isTrusted,
    };

    Sentry.captureException(e.error || new Error(errorInfo.message), {
        tags: {
            errorType: 'globalError',
            filename: errorInfo.filename,
            errorName: errorInfo.type,
            trusted: errorInfo.trusted ? 'true' : 'false',
        },
        extra: {
            ...errorInfo,
            eventType: 'error',
            errorObject: e.error,
            originalEvent: {
                type: e.type,
                timeStamp: e.timeStamp,
                bubbles: e.bubbles,
                cancelable: e.cancelable,
                defaultPrevented: e.defaultPrevented,
            },
        },
        level: 'error',
    });
});

window.addEventListener('unhandledrejection', (e) => {
    const error = e.reason;
    const isErrorObject = error instanceof Error;

    const errorInfo = {
        message: isErrorObject ? error.message : String(error),
        stack: isErrorObject ? error.stack : 'No stack trace available',
        name: isErrorObject ? error.name : typeof error,
        timestamp: new Date().toLocaleString('ko-KR'),
        userAgent: navigator.userAgent,
        url: window.location.href,
        promiseRejected: true,
        trusted: e.isTrusted,
    };

    const captureError = isErrorObject ? error : new Error(errorInfo.message);

    Sentry.captureException(captureError, {
        tags: {
            errorType: 'unhandledRejection',
            isErrorObject: isErrorObject ? 'true' : 'false',
            errorName: errorInfo.name,
            trusted: errorInfo.trusted ? 'true' : 'false',
        },
        extra: {
            ...errorInfo,
            eventType: 'unhandledrejection',
            originalReason: error,
            reasonType: typeof error,
            originalEvent: {
                type: e.type,
                timeStamp: e.timeStamp,
                bubbles: e.bubbles,
                cancelable: e.cancelable,
                defaultPrevented: e.defaultPrevented,
            },
        },
        level: 'error',
    });
});

window.addEventListener('abort', (e) => {
    const errorInfo = {
        message: 'Request aborted',
        timestamp: new Date().toLocaleString('ko-KR'),
        userAgent: navigator.userAgent,
        url: window.location.href,
        target: e.target?.toString() || 'Unknown target',
        trusted: e.isTrusted,
    };

    Sentry.captureException(new Error('Request Abort Event'), {
        tags: {
            errorType: 'abortEvent',
            trusted: errorInfo.trusted ? 'true' : 'false',
        },
        extra: {
            ...errorInfo,
            eventType: 'abort',
            originalEvent: {
                type: e.type,
                timeStamp: e.timeStamp,
                bubbles: e.bubbles,
                cancelable: e.cancelable,
                defaultPrevented: e.defaultPrevented,
                target: e.target,
            },
        },
        level: 'warning',
    });
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <>
        <Sentry.ErrorBoundary>
            <HelmetProvider>
                <CookiesProvider>
                    <ThemeProvider theme={theme}>
                        <RouterProvider router={router} fallbackElement={<MainLoading />} />
                        <ToastContainer />
                    </ThemeProvider>
                </CookiesProvider>
            </HelmetProvider>
        </Sentry.ErrorBoundary>
    </>,
);
